import { z } from "zod";
import { projectSettingSchema } from "./admin.ts";
import { projectGroupGrantSchema, projectRuleGrantSchema } from "./directory.ts";
import { documentStatusSchema } from "./knowledge.ts";
import {
  assignableProjectRoleSchema,
  projectIdSchema,
  projectMemberViewSchema,
  projectOwnerSchema,
  projectVisibilitySchema,
  updateProjectRequestSchema,
} from "./projects.ts";
import { taskCountsSchema } from "./tasks.ts";
import { roleSchema } from "./tenancy.ts";

/**
 * Project governance for tenant and entity admins (specs/projects "Governance without content
 * access"): metadata only, never document content, passages or conversation text. Personal
 * projects are listed to tenant admins only, without their names.
 */
export const governedProjectSchema = z.object({
  id: projectIdSchema,
  /** Null for a personal project: admins see that it exists, not what it is about. */
  name: z.string().nullable(),
  owner: projectOwnerSchema,
  visibility: projectVisibilitySchema,
  createdAt: z.iso.datetime(),
  frozenAt: z.iso.datetime().nullable(),
  members: z.int().min(0),
  /** Members added through break-glass. */
  breakGlassMembers: z.int().min(0),
  /** Roles granted to directory groups and to attribute rules (counts). */
  groupGrants: z.int().min(0),
  ruleGrants: z.int().min(0),
  /** Live documents per status. */
  documents: z.partialRecord(documentStatusSchema, z.int().min(0)),
  /** Bytes of live documents. */
  storageBytes: z.int().min(0),
  /**
   * When the project was created, or its documents or conversations last changed, whichever is
   * latest (a timestamp only). Governance changes (renames, freezes, overrides) do not count.
   * Null for a personal project, like its figures.
   */
  lastActivityAt: z.iso.datetime().nullable(),
  /** Live documents per classification level id. */
  classifications: z.record(z.string(), z.int().min(0)),
  /** Pages read by OCR across live documents (counts only). */
  ocrPages: z.int().min(0),
  /**
   * The last 30 days, from the usage rollup (counts only, never who): flow-check refusals in
   * the project, questions asked, distinct active users and estimated model cost. Null for a
   * personal project: there, they would be one person's figures.
   */
  flowDenials30d: z.int().min(0).nullable(),
  questions30d: z.int().min(0).nullable(),
  activeUsers30d: z.int().min(0).nullable(),
  costUsd30d: z.number().min(0).nullable(),
});
export type GovernedProject = z.infer<typeof governedProjectSchema>;

/** What governance actions return: the same redaction as the listing, no aggregates. */
export const governedProjectSummarySchema = governedProjectSchema.pick({
  id: true,
  name: true,
  owner: true,
  visibility: true,
  createdAt: true,
  frozenAt: true,
});
export type GovernedProjectSummary = z.infer<typeof governedProjectSummarySchema>;

/**
 * One governed entity project as the backoffice manages it: its row, who holds which role
 * (direct members, groups and rules) and its effective settings. Never any content; break-glass
 * reasons stay with the project's own admins.
 */
export const governedProjectDetailSchema = z.object({
  project: governedProjectSchema.extend({
    name: z.string(),
    description: z.string().nullable(),
  }),
  members: z.array(projectMemberViewSchema),
  groups: z.array(projectGroupGrantSchema),
  rules: z.array(projectRuleGrantSchema),
  settings: z.array(projectSettingSchema),
  /** Task figures (openspec change `tasks`): counts only, never a title. */
  tasks: taskCountsSchema,
});
export type GovernedProjectDetail = z.infer<typeof governedProjectDetailSchema>;

export const governedProjectsQuerySchema = z.object({
  /** Only projects owned by this entity or its descendants. */
  under: z.uuid().optional(),
});

/** To an entity the caller administers; members and roles are kept, visibility becomes `private`. */
export const transferProjectRequestSchema = z.object({ entityId: z.uuid() });
export type TransferProjectRequest = z.infer<typeof transferProjectRequestSchema>;

/** Governance reassignment: an existing direct member becomes the project's `owner`. */
export const reassignProjectOwnerRequestSchema = z.object({ userId: z.uuid() });
export type ReassignProjectOwnerRequest = z.infer<typeof reassignProjectOwnerRequestSchema>;

export const BREAK_GLASS_REASON_MAX_CHARS = 500;

/**
 * Break-glass (specs/projects "Audited break-glass access"): an admin who governs the project
 * adds themselves as `reader`, with a reason. A missing or blank reason is refused with `422`
 * by the service, so the schema only bounds its length.
 */
export const breakGlassRequestSchema = z.object({
  reason: z.string().max(BREAK_GLASS_REASON_MAX_CHARS).default(""),
});
export type BreakGlassRequest = z.infer<typeof breakGlassRequestSchema>;

/** The tenant's OCR use this calendar month (UTC) against its effective monthly budget. */
export const ocrUsageSchema = z.object({
  /** `YYYY-MM`. */
  month: z.string(),
  pagesUsed: z.int().min(0),
  /** min(plan limit, `knowledge.ocrMonthlyPages`); null = unlimited. */
  monthlyBudget: z.int().min(0).nullable(),
});
export type OcrUsage = z.infer<typeof ocrUsageSchema>;

// ── Access requests (openspec change `governance-access-approval`) ─────────────────────────

/** How long an access request waits for the project's decision before it expires. */
export const ACCESS_REQUEST_TTL_DAYS = 7;
export const ACCESS_REQUEST_REASON_MAX_CHARS = 500;

/**
 * A change that waits for a project's approval because it would widen who reads the project or
 * open it to the requesting admin. Replayed as the requester once approved.
 */
export const accessRequestChangeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("project.update"), input: updateProjectRequestSchema }),
  z.object({
    kind: z.literal("project.member_add"),
    userId: z.uuid(),
    role: assignableProjectRoleSchema,
  }),
  z.object({
    kind: z.literal("project.group_add"),
    groupId: z.uuid(),
    role: assignableProjectRoleSchema,
  }),
  z.object({
    kind: z.literal("project.group_role"),
    groupId: z.uuid(),
    role: assignableProjectRoleSchema,
  }),
  z.object({
    kind: z.literal("project.rule_grant"),
    ruleId: z.uuid(),
    role: assignableProjectRoleSchema,
  }),
  z.object({ kind: z.literal("project.setting"), key: z.string(), value: z.unknown() }),
  z.object({ kind: z.literal("project.transfer"), entityId: z.uuid() }),
  z.object({
    kind: z.literal("entity.member_add"),
    entityId: z.uuid(),
    roles: z.array(roleSchema).min(1),
  }),
  z.object({
    kind: z.literal("setting.put"),
    entityId: z.uuid().nullable(),
    key: z.string(),
    value: z.unknown(),
    locked: z.boolean(),
  }),
  z.object({ kind: z.literal("setting.reset"), entityId: z.uuid().nullable(), key: z.string() }),
  // Held reads (openspec change `governance-reparent-sync`): the move or sync already applied;
  // approval only releases the hold it put on the admin's read of the project. `role` is the
  // highest role the held paths (groups, rules, visibility) gave when the hold was taken: an
  // approval never releases more.
  z.object({
    kind: z.literal("access.entity_move"),
    entityId: z.uuid(),
    oldParentId: z.uuid().nullable(),
    newParentId: z.uuid().nullable(),
    role: assignableProjectRoleSchema,
  }),
  z.object({
    kind: z.literal("access.directory"),
    sourceId: z.uuid(),
    role: assignableProjectRoleSchema,
  }),
]);
export type AccessRequestChange = z.infer<typeof accessRequestChangeSchema>;

/** The change kinds of held reads: approval releases a hold, it never replays a change. */
export const HELD_READ_KINDS = ["access.entity_move", "access.directory"] as const;
export type HeldReadChange = Extract<
  AccessRequestChange,
  { kind: (typeof HELD_READ_KINDS)[number] }
>;
export function isHeldReadChange(change: AccessRequestChange): change is HeldReadChange {
  return (HELD_READ_KINDS as readonly string[]).includes(change.kind);
}

export const accessRequestStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "expired",
  "cancelled",
]);
export type AccessRequestStatus = z.infer<typeof accessRequestStatusSchema>;

export const accessRequestViewSchema = z.object({
  id: z.uuid(),
  /** Requests created by one change share it; the change applies once all are approved. */
  changeId: z.uuid(),
  projectId: projectIdSchema,
  /** Null for a personal project. */
  projectName: z.string().nullable(),
  requestedBy: z.object({
    userId: z.uuid(),
    email: z.string(),
    displayName: z.string().nullable(),
  }),
  change: accessRequestChangeSchema,
  /** The group, user, rule or entity the change names, when it names one. */
  subjectName: z.string().nullable(),
  reason: z.string().nullable(),
  status: accessRequestStatusSchema,
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  /** The other projects' decisions are not shown: only how many of the group approved. */
  group: z.object({ size: z.int().min(1), approved: z.int().min(0) }),
});
export type AccessRequestView = z.infer<typeof accessRequestViewSchema>;

/** What a change that waits for approval answers (`202`): nothing was applied. */
export const pendingApprovalSchema = z.object({
  status: z.literal("pending_approval"),
  changeId: z.uuid(),
  requests: z.array(accessRequestViewSchema),
});
export type PendingApproval = z.infer<typeof pendingApprovalSchema>;

export const accessRequestReasonRequestSchema = z.object({
  reason: z.string().trim().min(1).max(ACCESS_REQUEST_REASON_MAX_CHARS),
});
export type AccessRequestReasonRequest = z.infer<typeof accessRequestReasonRequestSchema>;
